/**
 * Группировка модулей в матрице прав - зеркало разделов бокового меню интерфейса
 * (/admin - AdminPanel.tsx, /dashboard - DashboardSidebar.tsx).
 * Модули, не попавшие ни в одну группу, выводятся в конце в разделе «Прочее».
 */
export interface MenuGroup {
  key: string;
  title: string;
  moduleIds: string[];
}

export type MatrixInterface = 'admin' | 'dashboard';

export const MENU_GROUPS: MenuGroup[] = [
  { key: 'home', title: 'Главная', moduleIds: ['home'] },
  { key: 'reports', title: 'Отчетность', moduleIds: ['reports'] },
  { key: 'documents', title: 'Документы', moduleIds: ['documents', 'timesheet', 'journal'] },
  { key: 'data', title: 'Данные', moduleIds: ['remains', 'nomenclature'] },
  { key: 'administration', title: 'Администрирование', moduleIds: ['users', 'create_account', 'tables', 'audit', 'system'] }
];

export const DASHBOARD_MENU_GROUPS: MenuGroup[] = [
  { key: 'home', title: 'Главная', moduleIds: ['home'] },
  { key: 'reports', title: 'Отчетность', moduleIds: ['reports'] },
  { key: 'documents', title: 'Документы', moduleIds: ['documents', 'timesheet', 'journal'] },
  { key: 'monitoring', title: 'Мониторинг', moduleIds: ['monitoring'] },
  { key: 'guide', title: 'Справочник', moduleIds: ['remains', 'nomenclature'] },
  { key: 'point', title: 'Точка', moduleIds: ['staff'] }
];

/**
 * Модули матрицы прав по интерфейсам. У /admin доступны все модули каталога, кроме тех,
 * в которых все права только для /dashboard; у /dashboard - только перечисленные.
 */
export const DASHBOARD_MODULE_IDS: string[] = [
  'home',
  'reports',
  'documents',
  'timesheet',
  'journal',
  'monitoring',
  'remains',
  'nomenclature',
  'staff'
];

/**
 * Права модулей, общих с /admin, которые показываются в матрице /dashboard. Сейчас кабинет аптеки
 * управляется на уровне «включить/выключить вкладку», остальные права этих модулей относятся к /admin.
 * Модуль из DASHBOARD_MODULE_IDS без записи здесь показывается целиком (кроме ADMIN_ONLY_PERMISSIONS).
 */
export const DASHBOARD_MODULE_PERMISSIONS: Record<string, string[]> = {
  reports: ['reports.menu', 'reports.current.view', 'reports.monthly.view'],
  documents: ['documents.menu', 'documents.flow.view', 'documents.marking.view'],
  timesheet: ['timesheet.menu'],
  journal: ['journal.menu'],
  remains: ['guide.remains.menu'],
  nomenclature: ['guide.nomenclature.menu']
};

/** Права, существующие только в одном интерфейсе (в матрице /admin не показываются) */
export const DASHBOARD_ONLY_PERMISSIONS: string[] = [
  'home.revenue.view',
  'home.report_today.view',
  // записи журнала сроков вносит и правит аптека: в /admin журнал только для просмотра
  'journal.create',
  'journal.edit',
  'journal.delete',
  // вкладки бокового меню кабинета аптеки
  'reports.current.view',
  'reports.monthly.view',
  'documents.flow.view',
  'monitoring.menu',
  'monitoring.cash.view',
  'monitoring.km.view',
  // подчинённые учётки точки: мастер управляет ими из кабинета, админка - через users.staff.*
  'staff.menu',
  'staff.create',
  'staff.create.limit',
  'staff.edit_permissions',
  'staff.reset_password',
  'staff.block',
  'staff.sessions',
  'staff.audit'
];

/** Права, существующие только в /admin (в матрице кабинета аптеки не показываются) */
export const ADMIN_ONLY_PERMISSIONS: string[] = [
  'home.reports.submission.view',
  'home.reports.submission.limit',
  'home.network_revenue.view'
];

const isVisibleIn = (targetInterface: MatrixInterface, moduleId: string, code: string): boolean => {
  if (targetInterface === 'admin') return !DASHBOARD_ONLY_PERMISSIONS.includes(code);
  const allowed = DASHBOARD_MODULE_PERMISSIONS[moduleId];
  return allowed ? allowed.includes(code) : !ADMIN_ONLY_PERMISSIONS.includes(code);
};

export function filterModulesForInterface<T extends { id: string; permissions: Array<{ code: string }> }>(
  modules: T[],
  targetInterface: MatrixInterface
): T[] {
  const base = targetInterface === 'dashboard' ? modules.filter(m => DASHBOARD_MODULE_IDS.includes(m.id)) : modules;
  return base
    .map(m =>
      m.permissions.every(p => isVisibleIn(targetInterface, m.id, p.code))
        ? m
        : { ...m, permissions: m.permissions.filter(p => isVisibleIn(targetInterface, m.id, p.code)) }
    )
    // модуль без прав этого интерфейса (например, «Мониторинг» в /admin) в матрице не нужен
    .filter(m => m.permissions.length > 0);
}

export const OTHER_GROUP: Pick<MenuGroup, 'key' | 'title'> = { key: 'other', title: 'Прочее' };

export interface GroupedEntry<T> {
  module: T;
  /** Индекс модуля в исходном массиве modules */
  index: number;
}

export interface GroupedModules<T> {
  key: string;
  title: string;
  entries: GroupedEntry<T>[];
}

export function groupModulesByMenu<T extends { id: string }>(modules: T[], targetInterface: MatrixInterface): GroupedModules<T>[] {
  const byId = new Map(modules.map((m, index) => [m.id, { module: m, index }]));
  const used = new Set<string>();
  const result: GroupedModules<T>[] = [];

  const groups = targetInterface === 'dashboard' ? DASHBOARD_MENU_GROUPS : MENU_GROUPS;
  groups.forEach(g => {
    const entries = g.moduleIds
      .map(id => byId.get(id))
      .filter((e): e is GroupedEntry<T> => Boolean(e));
    entries.forEach(e => used.add(e.module.id));
    if (entries.length) result.push({ key: g.key, title: g.title, entries });
  });

  const rest = modules
    .map((module, index) => ({ module, index }))
    .filter(e => !used.has(e.module.id));
  if (rest.length) result.push({ ...OTHER_GROUP, entries: rest });

  return result;
}
